import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const nav = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setTimeout(() => nav(`/order/${orderId || r.data.order_id}`), 1400);
          return;
        }
        if (attempts.current > 15) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current > 15) setStatus("failed");
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId, nav]);

  return (
    <div data-testid="payment-success" className="max-w-2xl mx-auto px-6 py-24 text-center">
      {status === "polling" && (
        <>
          <Loader2 size={48} className="text-[#D4AF37] mx-auto animate-spin"/>
          <div className="eyebrow mt-6">Confirming Your Payment</div>
          <h1 className="font-serif-luxe text-4xl text-[#FAF8F5] mt-3">Just a moment…</h1>
          <p className="text-[#C2B29F] mt-4">We're verifying your payment with Stripe.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto"/>
          <div className="eyebrow mt-6">Payment Received</div>
          <h1 className="font-serif-luxe text-4xl text-[#FAF8F5] mt-3">Your aura awaits ✦</h1>
          <p className="text-[#C2B29F] mt-4">Redirecting to your order confirmation…</p>
        </>
      )}
      {(status === "timeout" || status === "failed") && (
        <>
          <XCircle size={48} className="text-[#C2B29F] mx-auto"/>
          <div className="eyebrow mt-6">Still Processing</div>
          <h1 className="font-serif-luxe text-4xl text-[#FAF8F5] mt-3">Payment is taking longer than expected</h1>
          <p className="text-[#C2B29F] mt-4">You can view your order status from your account.</p>
          <Link to={orderId ? `/order/${orderId}` : "/account"} className="btn-gold mt-8 inline-flex">View Order</Link>
        </>
      )}
    </div>
  );
}
